import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "antd";
import { FallbackProps } from "react-error-boundary";
import styles from "./styles.module.scss";
import { LeftOutline } from "antd-mobile-icons";
import { useAppNavigate } from "../../../hooks/router";
import { analyticsController } from "../../../AppController";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../hooks/redux";

const { Title, Text } = Typography;

interface ErrorComponentProps extends FallbackProps {
  connectionStatus?: boolean; // kept for backwards-compat; ignored (read from Redux)
}

export const ErrorComponent: FC<ErrorComponentProps> = ({
  error,
  resetErrorBoundary,
}) => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();

  const { user, userClinic, connectionStatus } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
    connectionStatus: state.network.connectionStatus,
  }));

  const logErrorBoundary = (error: Error) => {
    const errorArr = error.stack?.split("\n");

    analyticsController.logEvent("error-boundry-exception", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
      location: location,
      error: {
        name: error.name,
        stack: errorArr,
        cause: error.cause,
        message: error.message,
      },
    });
  };

  useEffect(() => {
    // Check if the error is related to dynamic module fetching and the network is online
    if (
      connectionStatus &&
      error?.message.includes("dynamically imported module")
    ) {
      resetErrorBoundary();
      return;
    }

    if (error) {
      logErrorBoundary(error);
    }
  }, [connectionStatus, error, resetErrorBoundary]);

  if (!connectionStatus) {
    // Network connectivity issue
    return (
      <div className={styles.noWorryContainer}>
        <Title level={4} className={styles.heading}>
          {t("errorComponent.noInternet")}
        </Title>
        <Text className={styles.subText}>
          {t("errorComponent.noInternetDesc")}
        </Text>
      </div>
    );
  }

  // Show error for cases other than dynamic module fetching issues
  if (error && !error.message.includes("dynamically imported module")) {
    return (
      <>
        <span id="back-icon" className={styles.backButton}>
          <LeftOutline
            className={styles.backIcon}
            onClick={() => navigate(-1)}
          />
        </span>
        <div className={styles.noWorryContainer}>
          <Title level={4} className={styles.heading}>
            {t("errorComponent.somethingWrong")}
          </Title>
        </div>
      </>
    );
  }
  // No error to display or connectivity issue, return null
  return null;
};
